import { useState, useEffect } from 'react';
import { api } from '../../utils/api';
import { formatDate } from '../../utils/formatters';
import { ScrollText, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuthStore } from '@/stores/auth';

interface AuditLog {
  id: string;
  action: string;
  entity: string;
  entityId: string | null;
  tenantId: string | null;
  tenantName: string;
  createdAt: string;
  ip: string | null;
  changes: any;
  user: { firstName: string; lastName: string; email: string } | null;
}

const ACTION_COLORS: Record<string, string> = {
  create: 'bg-emerald-100 text-emerald-700',
  update: 'bg-blue-100 text-blue-700',
  delete: 'bg-red-100 text-red-700',
  login: 'bg-violet-100 text-violet-700',
  logout: 'bg-gray-100 text-gray-600',
};

export function AuditLogsPage() {
  // Tour bug B-AD-03: a tenant admin gets his own company's log (server-scoped);
  // only the platform admin reads across tenants and can filter by one.
  const { user } = useAuthStore();
  const isPlatform = user?.email === 'admin@aura.ardhanex.com' || user?.email === 'admin@aura.ardhanex.info'
    || (user?.email ?? '').endsWith('@ardhanex.com');
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const [tenantFilter, setTenantFilter] = useState('');
  const limit = 50;

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
    if (tenantFilter) params.set('tenant', tenantFilter);

    api.get(`/admin/audit-logs?${params}`)
      .then(res => {
        setLogs(res.data.data);
        setTotal(res.data.meta.total);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [offset, tenantFilter]);

  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(offset / limit) + 1;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Audit Logs</h1>
          <p className="text-sm text-gray-500">{isPlatform ? 'Activity log across all tenants' : 'Activity log for your company'}</p>
        </div>
        {isPlatform && (
          <div className="flex items-center gap-2">
            <input
              type="text"
              placeholder="Filter by tenant ID..."
              className="w-56 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
              value={tenantFilter}
              onChange={e => { setTenantFilter(e.target.value); setOffset(0); }}
            />
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex h-32 items-center justify-center">
          <div className="h-6 w-6 animate-spin rounded-full border-4 border-[var(--ink-0)] border-t-transparent" />
        </div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="px-4 py-3 font-medium text-gray-500">User</th>
                  <th className="px-4 py-3 font-medium text-gray-500">Action</th>
                  <th className="px-4 py-3 font-medium text-gray-500">Entity</th>
                  <th className="px-4 py-3 font-medium text-gray-500">Entity ID</th>
                  <th className="px-4 py-3 font-medium text-gray-500">Tenant</th>
                  <th className="px-4 py-3 font-medium text-gray-500">IP</th>
                  <th className="px-4 py-3 font-medium text-gray-500">Time</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {logs.map(log => (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2.5 text-gray-700">
                      {log.user ? (
                        <div>
                          <p className="font-medium">{log.user.firstName} {log.user.lastName}</p>
                          <p className="text-[10px] text-gray-400">{log.user.email}</p>
                        </div>
                      ) : 'System'}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-medium ${
                        ACTION_COLORS[log.action] || 'bg-gray-100 text-gray-600'
                      }`}>
                        {log.action}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 capitalize text-gray-600">{log.entity}</td>
                    <td className="px-4 py-2.5 text-xs text-gray-400 font-mono">
                      {log.entityId ? log.entityId.slice(0, 8) + '...' : '--'}
                    </td>
                    <td className="px-4 py-2.5 text-gray-600 text-xs">{log.tenantName}</td>
                    <td className="px-4 py-2.5 text-xs text-gray-400 font-mono">{log.ip || '--'}</td>
                    <td className="px-4 py-2.5 text-xs text-gray-500">{formatDate(log.createdAt)}</td>
                  </tr>
                ))}
                {logs.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-gray-400">
                      <ScrollText size={24} className="mx-auto mb-2 text-gray-300" />
                      No audit logs found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-500">
                Showing {offset + 1}–{Math.min(offset + limit, total)} of {total}
              </p>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setOffset(Math.max(0, offset - limit))}
                  disabled={offset === 0}
                  className="rounded p-1 text-gray-400 hover:bg-gray-100 disabled:opacity-30"
                >
                  <ChevronLeft size={16} />
                </button>
                <span className="px-2 text-xs text-gray-600">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setOffset(offset + limit)}
                  disabled={offset + limit >= total}
                  className="rounded p-1 text-gray-400 hover:bg-gray-100 disabled:opacity-30"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
